import getConfig from 'next/config';
import renderElements from '~/helpers/renderer/renderer';
import getCoreServerSideProps from '../modules/core/getCoreServerSideProps';
import dynamic from 'next/dynamic';

const CareerPageRenderer = dynamic(
  () =>
    import('~/modules/career-pages/helpers/CareerPageRenderer').then(
      (mod) => mod.CareerPageRenderer,
    ),
  {
    ssr: false,
  },
);

/**
 * Fetches server-side props for the regular page.
 *
 * This function retrieves core server-side properties required to render the page.
 *
 * @param {Object} ctx - The Next.js context object.
 * @returns {Promise<Object>} A promise that resolves to the props for the page.
 */
export const getServerSideProps = async (ctx) => {
  return {
    props: getCoreServerSideProps(ctx),
  };
};

/**
 * RegularNextPage Component
 *
 * This component renders a regular page using the `CareerPageRenderer` component.
 * It dynamically renders elements based on the page content and supports additional features like:
 * - Breadcrumb navigation
 * - Social sharing options
 * - Content hub category styling
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.pageContent - The content data for the page.
 * @param {string | undefined} [props.pageContent.style] - The style of the page (e.g., "CONTENT_HUB_TOPIC").
 * @param {boolean} [props.pageContent.withBreadcrumb] - Whether to display breadcrumb navigation.
 * @param {boolean} [props.pageContent.withShare] - Whether to display social sharing options.
 * @param {Object} props.context - Contextual information, such as locale and edit mode.
 * @returns {React.ReactElement} The rendered regular page.
 */
function RegularNextPage(props) {
  const { publicRuntimeConfig } = getConfig();
  const pageStyle = props?.pageContent?.style;
  return (
    <CareerPageRenderer
      {...props}
      withBreadcrumb={props.pageContent.withBreadcrumb}
      withFootShare={props.pageContent.withShare}
      style={pageStyle}
    >
      {renderElements(props.pageContent.content, undefined, {
        cmsBaseUrl: publicRuntimeConfig.cmsBaseUrl,
        lang: props.context?.locale,
        isEditMode: 'isEditMode' in props.context && props.context.isEditMode,
        isContentHubCategoryPage: pageStyle === 'CONTENT_HUB_TOPIC',
      })}
    </CareerPageRenderer>
  );
}

export default RegularNextPage;
